import {
  Button,
  InlineNotification,
  Stack,
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
  TextArea,
} from '@carbon/react';
import { asProblem, BackLink, PageHeader, problemMessage, StatusTag, useFormat } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { ErrorNotice } from '../common/Notices';
import {
  getSubmission,
  STATUS_TAG,
  submissionKey,
  updateSubmission,
  type FormSubmissionDetail,
  type SubmissionStatus,
} from './formSubmissionsApi';

/** One form submission (CMS phase 2): the answers as the visitor gave them (opening it is audited), marked handled or spam with a note. */
export function FormSubmissionPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const submission = useQuery({ queryKey: submissionKey(id), queryFn: () => getSubmission(id) });

  if (submission.error) return <ErrorNotice title={problemMessage(t, asProblem(submission.error))} onRetry={() => void submission.refetch()} />;
  if (!submission.data) return null;
  return <Detail key={submission.data.id} submission={submission.data} />;
}

function Detail({ submission: s }: { submission: FormSubmissionDetail }) {
  const { t } = useTranslation();
  const { dateTime } = useFormat();
  const queryClient = useQueryClient();
  const [note, setNote] = useState(s.note ?? '');
  const update = useMutation({
    mutationFn: (status: SubmissionStatus) => updateSubmission(s.id, status, note),
    onSuccess: (next) => {
      queryClient.setQueryData(submissionKey(s.id), next);
      void queryClient.invalidateQueries({ queryKey: ['ho', 'form-submissions'] });
    },
  });
  const status = s.status as SubmissionStatus;
  const answer = (value: string) => (value === 'true' ? t('formSubmissions.yes') : value === 'false' ? t('formSubmissions.no') : value || '—');

  return (
    <section className="pos-admin-page">
      <BackLink to="/cms/submissions">{t('formSubmissions.title')}</BackLink>
      <PageHeader
        title={s.formTitle ?? t('formSubmissions.title')}
        description={t('formSubmissions.sentAt', { when: dateTime(s.submittedAt) })}
        actions={
          <>
            {status !== 'handled' && (
              <Button kind="primary" disabled={update.isPending} onClick={() => update.mutate('handled')}>
                {t('formSubmissions.markHandled')}
              </Button>
            )}
            {status !== 'spam' && (
              <Button kind="tertiary" disabled={update.isPending} onClick={() => update.mutate('spam')}>
                {t('formSubmissions.markSpam')}
              </Button>
            )}
            {status !== 'new' && (
              <Button kind="ghost" disabled={update.isPending} onClick={() => update.mutate('new')}>
                {t('formSubmissions.reopen')}
              </Button>
            )}
          </>
        }
      />
      <Stack gap={6}>
        {update.error && <ErrorNotice title={problemMessage(t, asProblem(update.error))} />}
        {update.isSuccess && (
          <InlineNotification kind="success" lowContrast role="status" title={t('formSubmissions.saved')} onClose={() => update.reset()} />
        )}
        <StructuredListWrapper isCondensed aria-label={t('formSubmissions.answers')}>
          <StructuredListBody>
            <StructuredListRow>
              <StructuredListCell noWrap>{t('formSubmissions.columns.status')}</StructuredListCell>
              <StructuredListCell>
                <StatusTag status={STATUS_TAG[status] ?? 'inactive'}>{t(`formSubmissions.status.${status}`)}</StatusTag>
              </StructuredListCell>
            </StructuredListRow>
            {s.values.map((v) => (
              <StructuredListRow key={v.key}>
                <StructuredListCell noWrap>{v.label}</StructuredListCell>
                <StructuredListCell>{answer(v.value)}</StructuredListCell>
              </StructuredListRow>
            ))}
            <StructuredListRow>
              <StructuredListCell noWrap>{t('formSubmissions.consent')}</StructuredListCell>
              <StructuredListCell>{s.consent ? t('formSubmissions.yes') : t('formSubmissions.no')}</StructuredListCell>
            </StructuredListRow>
            {s.handledAt && (
              <StructuredListRow>
                <StructuredListCell noWrap>{t('formSubmissions.handledAt')}</StructuredListCell>
                <StructuredListCell>{dateTime(s.handledAt)}</StructuredListCell>
              </StructuredListRow>
            )}
          </StructuredListBody>
        </StructuredListWrapper>
        <TextArea
          id="form-submission-note"
          labelText={t('formSubmissions.note')}
          helperText={t('formSubmissions.noteHelp')}
          maxCount={1000}
          enableCounter
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Stack>
    </section>
  );
}
